<template>
  <div class="iw-page">
    <MiniLoader v-if="status === 'loading'" />

    <div v-else-if="status === 'error'" class="iw-state">
      <p>Não foi possível carregar os dados de pesagem individual.</p>
      <button class="iw-retry" @click="load">Tentar novamente</button>
    </div>

    <div v-else-if="status === 'ready' && !raw" class="iw-state">
      <p>Sem dados de pesagem individual para esta baia. Contate o suporte PecSmart.</p>
    </div>

    <div v-else-if="raw">
      <div class="iw-demo" v-if="raw.ejemplo">
        <span class="iw-demo-tag">DADOS DE EXEMPLO</span>
        <span>Estrutura definitiva; valores ilustrativos até a conexão com a câmera ser validada.</span>
      </div>

      <header class="iw-top">
        <div>
          <div class="iw-eyebrow">{{raw.empresa ? raw.empresa + ' · ' : ''}}Pesagem individual</div>
          <h2 class="iw-title">{{raw.granja}}</h2>
          <div class="iw-sub">
            <b>{{raw.galpon}}</b> · {{raw.camaras.length}} câmeras SmartCam · {{raw.grupos.length}} grupos de entrada
            <template v-if="raw.peso_objetivo_kg != null"> · peso objetivo <b>{{fmt(raw.peso_objetivo_kg,0)}} kg</b></template>
          </div>
        </div>
      </header>

      <section>
        <div class="iw-sec-head">
          <h3>Galpão</h3>
          <span class="iw-hint">Indicadores das baias alojadas, ponderados por quantidade de animais</span>
        </div>
        <div class="iw-kpis" v-if="kpis">
          <div class="iw-kpi">
            <div class="iw-eyebrow">Animais alojados</div>
            <div class="iw-kpi-v">{{kpis.animales}}</div>
            <div class="iw-kpi-d">{{kpis.corralesActivos}} baias ativas · {{grupsAlojados.length}} grupo(s) de {{raw.grupos.length}}</div>
          </div>
          <div class="iw-kpi">
            <div class="iw-eyebrow">Peso médio atual</div>
            <div class="iw-kpi-v">{{fmt(kpis.pesoMedio,2)}}<small>kg</small></div>
            <div class="iw-kpi-d" v-if="kpis.vspic != null"><span :class="favClass(kpis.vspic)">{{pct(kpis.vspic)}}</span> vs curva de referência</div>
            <div class="iw-kpi-d" v-else>média da última pesagem das baias alojadas</div>
          </div>
          <div class="iw-kpi" v-if="raw.peso_objetivo_kg != null">
            <div class="iw-eyebrow">Dia estimado ao objetivo</div>
            <div class="iw-kpi-v">{{kpis.diasObj != null ? Math.round(kpis.diasObj) : "—"}}</div>
            <div class="iw-kpi-d">{{fmt(raw.peso_objetivo_kg,0)}} kg de meta</div>
          </div>
        </div>
      </section>

      <section>
        <div class="iw-sec-head">
          <h3>Entradas por grupo</h3>
          <span class="iw-hint">Cada grupo reúne as câmeras designadas na entrada do lote</span>
        </div>
        <div class="iw-grupos">
          <article class="iw-grupo" :class="{alojado: g.estado==='alojado'}" v-for="g in orderedGrupos" :key="g.grupo" @click="openGroup(g)">
            <div class="iw-grupo-head">
              <span class="iw-grupo-title">Grupo {{g.grupo}}</span>
              <span class="iw-chip" :class="g.estado==='alojado' ? 'ok' : 'neutral'"><i class="dot"></i>{{groupStatusLabel(g)}}</span>
            </div>
            <div class="iw-grupo-date" v-if="g.estado==='nao_confirmado'">entrada ainda não confirmada</div>
            <div class="iw-grupo-date" v-else>{{g.estado==='alojado' ? 'Entrada' : 'Entrada prevista'}} <b>{{fdateL(g.fecha)}}</b></div>
            <dl v-if="g.estado==='alojado'">
              <dt>Animais</dt><dd>{{g.animales}}</dd>
              <dt>Peso na entrada</dt><dd>{{fmt(groupEntryAverage(g),2)}} kg</dd>
              <dt>Peso médio</dt><dd>{{fmt(groupAverage(g),2)}} kg</dd>
              <dt>Idade na entrada</dt><dd>{{g.edad}} dias</dd>
              <dt>Idade atual</dt><dd>{{fmt(groupCurrentAge(g),0)}} dias</dd>
              <dt>Baias</dt><dd>{{g.corrales.length}}</dd>
            </dl>
            <dl v-else>
              <dt>Animais</dt><dd class="empty">a confirmar</dd>
              <dt>Peso na entrada</dt><dd class="empty">—</dd>
              <dt>Peso médio</dt><dd class="empty">—</dd>
              <dt>Idade na entrada</dt><dd class="empty">—</dd>
              <dt>Idade atual</dt><dd class="empty">—</dd>
              <dt>Baias</dt><dd>{{g.corrales.length || '—'}}</dd>
            </dl>
            <div class="iw-cams">
              <span class="iw-cam-tag" :class="{on: g.estado==='alojado'}" v-for="cam in g.camaras" :key="cam">Câm. {{cam}}</span>
            </div>
          </article>
        </div>
      </section>

      <section>
        <div class="iw-sec-head">
          <h3>Comparativo de baias</h3>
          <span class="iw-hint">Clique numa coluna para ordenar</span>
        </div>
        <div class="iw-tabla-wrap">
          <table class="iw-tabla">
            <thead>
              <tr>
                <th v-for="col in tableColumns" :key="col.k" @click="sortBy(col.k)" :class="{l: col.l}">
                  {{col.label}}<span v-if="sortK===col.k">{{sortDir==='asc' ? ' ↑' : ' ↓'}}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in sortedCorrales" :key="c.corral" @click="openDetalle(c.corral)" tabindex="0" @keydown.enter="openDetalle(c.corral)">
                <td class="code">{{c.corral}}</td>
                <td class="num">{{c.grupo}}</td>
                <td class="l">Câm. {{c.camara}} · {{c.lado === 'izquierdo' ? 'esq.' : 'dir.'}}</td>
                <td class="l">{{c.sexo}}</td>
                <td class="num">{{c.cantidad}}</td>
                <td class="num">{{fmt(c.peso_ingreso,1)}}</td>
                <td class="num">{{dash(c.idade)}}</td>
                <td class="num"><b>{{fmt(c.actual,1)}}</b></td>
                <td class="num">{{fmt(c.peso_referencia_idade,1)}}</td>
                <td class="num">{{dash(c.dias_alojado)}}</td>
                <td class="num">{{fmt(c.gmd,2)}}</td>
                <td class="num"><span class="iw-delta" :class="favClass(c.vspic)">{{pct(c.vspic)}}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <footer class="iw-footer">
        <div class="iw-legend" v-if="raw.penWeights">
          <span><i class="iw-sw obs"></i>Peso previsto</span>
          <span><i class="iw-sw band"></i>Faixa p10–p90</span>
          <span><i class="iw-sw flag"></i>Dia em que a curva seguiu a referência</span>
        </div>
        <div class="iw-legend" v-else>
          <span><i class="iw-sw obs"></i>Observado (SmartCam)</span>
          <span><i class="iw-sw proj"></i>Projeção</span>
          <span><i class="iw-sw band"></i>Faixa p10–p90</span>
          <span><i class="iw-sw ref"></i>Curva de referência</span>
          <span><b class="good-txt">Verde</b>&nbsp;melhor que a referência · <b class="bad-txt">vermelho</b>&nbsp;pior · sem cor abaixo de 2%</span>
        </div>
      </footer>

      <ModalStyle>
        <div v-if="detalheCorral" class="iw-dialog-backdrop" @click.self="closeDetalle">
          <div class="iw-dialog">
            <div class="iw-dialog-head">
              <div>
                <h3>Baia {{detalheCorral.corral}}</h3>
                <div class="iw-dlg-meta">
                  <span>{{detalheCorral.sexo}}</span>
                  <span v-if="detalheCorral.genetica">genética {{detalheCorral.genetica}}</span>
                  <span v-if="detalheCorral.cantidad != null">{{detalheCorral.cantidad}} animais</span>
                  <span>Câmera {{detalheCorral.camara}} · {{detalheCorral.lado === 'izquierdo' ? 'esquerdo' : 'direito'}}</span>
                  <span v-if="detalheCorral.fecha_ingreso">Grupo {{detalheCorral.grupo}} · entrada {{fdateL(detalheCorral.fecha_ingreso)}}<template v-if="detalheCorral.edad_ingreso != null"> ({{detalheCorral.edad_ingreso}} d)</template></span>
                  <span v-else>Grupo {{detalheCorral.grupo}}</span>
                </div>
              </div>
              <button class="iw-close" aria-label="Fechar" @click="closeDetalle">×</button>
            </div>
            <p v-if="detalheCorral.confirmed === false" class="iw-unconfirmed-note">entrada ainda não confirmada</p>
            <template v-else>
            <p v-if="detalheLoading" class="iw-unconfirmed-note">Carregando curva do curral...</p>
            <div v-else class="iw-dlg-kpis">
              <div class="iw-dk iw-dk-main"><div class="iw-k">Peso atual</div><div class="iw-v">{{fmt(detalheCorral.actual,1)}}<small> kg</small></div></div>
              <div class="iw-dk" v-if="detalheCorral.vspic != null"><div class="iw-k">vs curva de referência</div><div class="iw-v"><span :class="favClass(detalheCorral.vspic)">{{pct(detalheCorral.vspic)}}</span></div></div>
              <div class="iw-dk"><div class="iw-k">Ganho 7 dias</div><div class="iw-v">{{fmt(detalheCorral.gmd,2)}}<small> kg/d</small></div></div>
              <div class="iw-dk" v-if="raw.peso_objetivo_kg != null"><div class="iw-k">Dia ao objetivo</div><div class="iw-v">{{dash(detalheCorral.dias_obj)}}<small> · {{fdate(detalheCorral.fecha_obj)}}</small></div></div>
            </div>
            <p v-if="!detalheLoading && !detalheCorral.s.length" class="iw-unconfirmed-note">Ainda sem pesagem neste curral.</p>
            <div v-else-if="!detalheLoading" class="iw-chart-panel">
              <div class="iw-legend iw-legend-chart" v-if="raw.penWeights">
                <span><i class="iw-sw obs"></i>Peso previsto</span>
                <span><i class="iw-sw band"></i>p10–p90</span>
                <span><i class="iw-sw flag"></i>Seguiu a referência</span>
              </div>
              <div class="iw-legend iw-legend-chart" v-else>
                <span><i class="iw-sw obs"></i>Observado</span><span><i class="iw-sw proj"></i>Projeção</span>
                <span><i class="iw-sw band"></i>p10–p90</span><span><i class="iw-sw ref"></i>Referência</span>
              </div>
              <div class="iw-chart-wrap">
                <svg class="iw-chart" ref="chart" viewBox="0 0 800 360" role="img" aria-label="Curva de peso da baia" v-html="chartSvg"></svg>
                <div class="iw-tip" ref="tip"></div>
              </div>
            </div>
            </template>
          </div>
        </div>
      </ModalStyle>
    </div>
  </div>
</template>

<script>
import MiniLoader from './MiniLoader.vue'
import ModalStyle from './ModalStyle.vue'
import {
  fmt, fdate, fdateL, addDays, pct, favClass, deriveCorrales, deriveKpisGalpon, penWeightFlagNote,
  averageLatestWeight, averageEntryWeight, averageCurrentAge,
} from '../utils/individual-weighing'
import { sensorScreenBlocked } from '../utils/feedInventoryAccess'

export default {
  name: 'IndividualWeighingPage',
  props: ['barn', 'batch'],
  components: { MiniLoader, ModalStyle },
  data() {
    return {
      status: 'loading',
      detalheCorral: null,
      detalheLoading: false,
      sortK: 'corral',
      sortDir: 'asc',
      barnTarget: null,
    }
  },
  computed: {
    raw() {
      return this.$store.getters.getIndividualWeighingData
    },
    corrales() {
      if (!this.raw) return []
      return deriveCorrales(this.raw)
    },
    byCode() {
      return Object.fromEntries(this.corrales.map(c => [c.corral, c]))
    },
    kpis() {
      if (!this.raw) return null
      return deriveKpisGalpon(this.raw, this.corrales)
    },
    orderedGrupos() {
      return [...(this.raw?.grupos || [])].sort((a, b) =>
        String(a.grupo).localeCompare(String(b.grupo), 'pt-BR', { numeric: true })
      )
    },
    grupsAlojados() {
      return (this.raw?.grupos || []).filter(g => g.estado === 'alojado')
    },
    tableColumns() {
      return [
        { k: 'corral', label: 'Baia', l: true },
        { k: 'grupo', label: 'Grupo' },
        { k: 'camara', label: 'Câmera · lado', l: true },
        { k: 'sexo', label: 'Sexo', l: true },
        { k: 'cantidad', label: 'Qtd.' },
        { k: 'peso_ingreso', label: 'Entrada kg' },
        { k: 'idade', label: 'Idade (d)' },
        { k: 'actual', label: 'Atual kg' },
        { k: 'peso_referencia_idade', label: 'Peso ref. kg' },
        { k: 'dias_alojado', label: 'Dias alojado' },
        { k: 'gmd', label: 'GPD kg/d' },
        { k: 'vspic', label: 'vs ref.' },
      ]
    },
    sortedCorrales() {
      const k = this.sortK
      const dir = this.sortDir === 'asc' ? 1 : -1
      return [...this.corrales].sort((a, b) => {
        const va = a[k]
        const vb = b[k]
        if (typeof va === 'string') return va.localeCompare(vb) * dir
        return (((va ?? -1e9) - (vb ?? -1e9))) * dir
      })
    },
    chartSvg() {
      if (!this.detalheCorral || !this.raw) return ''
      if (this.raw.penWeights) return this.buildPenWeightsChartSvg(this.detalheCorral)
      return this.buildChartSvg(this.detalheCorral)
    },
  },
  methods: {
    fmt, fdate, fdateL, addDays, pct, favClass,
    dash(v) {
      return v == null ? '—' : v
    },
    groupAverage(group) {
      const pens = (group?.corrales || []).map(code => this.byCode[code]).filter(Boolean)
      const average = averageLatestWeight(pens)
      return average ?? group?.peso_medio
    },
    groupEntryAverage(group) {
      const pens = (group?.corrales || []).map(code => this.byCode[code]).filter(Boolean)
      return averageEntryWeight(pens)
    },
    groupCurrentAge(group) {
      const pens = (group?.corrales || []).map(code => this.byCode[code]).filter(Boolean)
      return averageCurrentAge(pens)
    },
    groupStatusLabel(group) {
      if (group.estado === 'alojado') return 'Alojado'
      if (group.estado === 'nao_confirmado') return 'Não confirmado'
      return 'Previsto'
    },
    weighingLabels() {
      const barn = this.barnTarget
      const farms = this.$store.getters.getAllBarns || []
      let granja = ''
      for (const entry of farms) {
        const barns = entry?.barn || []
        if (barns.some(item => String(item.pk) === String(barn?.pk))) {
          granja = entry?.farm?.name || entry?.name || ''
          break
        }
      }
      return {
        granja,
        galpon: barn?.name || '',
        empresa: '',
      }
    },
    async load() {
      this.status = 'loading'
      const barnPk = this.barnTarget?.pk
      const batchPk = this.batch?.pk ?? this.batch?.id ?? this.$store.getters.getBatchTarget?.pk ?? null
      if (barnPk == null && batchPk == null) {
        this.status = 'error'
        return
      }
      try {
        await this.$store.dispatch('fetchIndividualWeighingData', {
          barnPk,
          batchPk,
          labels: this.weighingLabels(),
        })
        this.status = 'ready'
        this.loadPenSeries()
      } catch (err) {
        console.error('IndividualWeighingPage:', err)
        this.status = 'error'
      }
    },
    sortBy(k) {
      if (k === this.sortK) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortK = k
        this.sortDir = ['corral', 'camara', 'sexo'].includes(k) ? 'asc' : 'desc'
      }
    },
    async loadPenSeries() {
      if (!this.raw?.penWeights) return
      const batchPk = this.batch?.pk ?? this.batch?.id ?? this.$store.getters.getBatchTarget?.pk
      const barnPk = this.barnTarget?.pk
      const missing = this.corrales.filter(c => c.confirmed !== false && !(c.s || []).length)
      if (!missing.length || batchPk == null) return

      await Promise.all(missing.map(async (c) => {
        try {
          await this.$store.dispatch('fetchIndividualWeighingPen', {
            batchPk,
            pen: c.corral,
            barnPk,
          })
        } catch (err) {
          console.error('IndividualWeighingPage pen series:', c.corral, err)
        }
      }))
    },
    openGroup(group) {
      const codes = group?.corrales || []
      const pen = codes
        .map(code => this.byCode[code])
        .find(item => item && item.confirmed !== false)
        || this.byCode[codes[0]]
      if (pen) this.openDetalle(pen.corral)
    },
    async openDetalle(code) {
      const c = this.byCode[code]
      if (!c) return
      this.detalheCorral = c
      const needsSeries = this.raw?.penWeights && c.confirmed !== false && !(c.s || []).length
      if (needsSeries) {
        this.detalheLoading = true
        try {
          const batchPk = this.batch?.pk ?? this.batch?.id ?? this.$store.getters.getBatchTarget?.pk
          await this.$store.dispatch('fetchIndividualWeighingPen', {
            batchPk,
            pen: c.corral,
            barnPk: this.barnTarget?.pk,
          })
          this.detalheCorral = this.byCode[c.corral] || c
        } catch (err) {
          console.error('IndividualWeighingPage pen:', err)
        } finally {
          this.detalheLoading = false
        }
      }
      this.$nextTick(this.attachChartTooltip)
    },
    closeDetalle() {
      this.detalheCorral = null
    },
    chartGeometry(c) {
      const raw = this.raw
      const W = 800, H = 360, L = 48, R = 16, T = 14, B = 40
      const horizon = c.dias_obj ? Math.min(c.dias_obj + 10, c.s.length) : c.s.length
      const s = c.s.filter(r => r.dia_lote <= horizon)
      if (!s.length) return null
      const ys = s.flatMap(r => [r.p10, r.p90, r.pic_kg]).concat([raw.peso_objetivo_kg])
      const y0 = Math.floor(Math.min(...ys) / 10) * 10
      const y1 = Math.ceil(Math.max(...ys) / 10) * 10
      const X = d => L + (d - 1) / (horizon - 1) * (W - L - R)
      const Y = v => T + (y1 - v) / (y1 - y0) * (H - T - B)
      const obs = s.filter(r => r.fase === 'observado')
      const proj = s.filter(r => r.fase === 'proyectado')
      const last = obs[obs.length - 1]
      if (!last) return null
      const projAll = [last, ...proj]
      const byDay = Object.fromEntries(s.map(r => [r.dia_lote, r]))
      return { raw, W, H, L, R, T, B, horizon, s, y0, y1, X, Y, obs, proj, last, projAll, byDay }
    },
    buildPenWeightsChartSvg(c) {
      const s = (c.s || []).filter(row => row.peso_kg != null && row.fecha)
      if (!s.length) return ''
      const W = 800
      const H = 360
      const L = 52
      const R = 16
      const T = 16
      const B = 46
      const values = s.flatMap(row => [row.peso_kg, row.p10, row.p90]).filter(value => value != null && !Number.isNaN(value))
      const min = Math.min(...values)
      const max = Math.max(...values)
      const pad = Math.max(1, (max - min) * 0.08)
      const y0 = min - pad
      const y1 = max + pad
      const t0 = Date.parse(s[0].fecha)
      const t1 = Date.parse(s[s.length - 1].fecha)
      const span = Math.max(t1 - t0, 1)
      const X = row => {
        if (s.length === 1) return L + (W - L - R) / 2
        return L + (Date.parse(row.fecha) - t0) / span * (W - L - R)
      }
      const Y = value => T + (y1 - value) / (y1 - y0) * (H - T - B)
      const path = (arr, key) => arr.map((row, index) => `${index ? 'L' : 'M'}${X(row).toFixed(1)},${Y(row[key]).toFixed(1)}`).join('')
      const bandRows = s.filter(row => row.p10 != null && row.p90 != null)
      const band = bandRows.length
        ? `M${bandRows.map(row => `${X(row).toFixed(1)},${Y(row.p90).toFixed(1)}`).join('L')}L${[...bandRows].reverse().map(row => `${X(row).toFixed(1)},${Y(row.p10).toFixed(1)}`).join('L')}Z`
        : ''
      const spanKg = y1 - y0
      const step = spanKg > 80 ? 20 : spanKg > 40 ? 10 : spanKg > 15 ? 5 : 2
      let grid = ''
      const firstTick = Math.ceil(y0 / step) * step
      for (let value = firstTick; value <= y1; value += step) {
        grid += `<line class="iw-grid" x1="${L}" x2="${W - R}" y1="${Y(value)}" y2="${Y(value)}"/><text x="${L - 8}" y="${Y(value) + 4}" text-anchor="end">${fmt(value, step < 1 ? 1 : 0)}</text>`
      }
      const tickCount = Math.min(6, s.length)
      const ticks = []
      for (let index = 0; index < tickCount; index += 1) {
        const row = s[Math.round(index * (s.length - 1) / Math.max(tickCount - 1, 1))]
        if (!ticks.length || ticks[ticks.length - 1] !== row) ticks.push(row)
      }
      const xt = ticks.map(row => `<line class="iw-axis" x1="${X(row)}" x2="${X(row)}" y1="${H - B}" y2="${H - B + 5}"/><text x="${X(row)}" y="${H - B + 18}" text-anchor="middle">${fdate(row.fecha)}</text>`).join('')
      const last = s[s.length - 1]
      const chip = (x, y, text) => {
        const w = text.length * 6.2 + 14
        return `<rect class="iw-chart-chip-bg" x="${x.toFixed(1)}" y="${(y - 12).toFixed(1)}" width="${w.toFixed(1)}" height="17" rx="3"/><text x="${x + 7}" y="${y}" text-anchor="start" class="iw-chart-chip-text iw-chart-chip-ink">${text}</text>`
      }
      return `
        <text x="${L - 8}" y="${T - 2}" text-anchor="end" class="iw-chart-unit">kg</text>
        ${grid}<line class="iw-axis" x1="${L}" x2="${W - R}" y1="${H - B}" y2="${H - B}"/>${xt}
        <path d="${band}" class="iw-band"/>
        <path d="${path(s, 'peso_kg')}" class="iw-obs-line"/>
        ${s.map(row => `<circle cx="${X(row)}" cy="${Y(row.peso_kg)}" r="${row.flag ? 4.4 : 3.2}" class="${row.flag ? 'iw-flag-dot' : 'iw-obs-dot'}"/>`).join('')}
        <line x1="${X(last)}" x2="${X(last)}" y1="${T}" y2="${H - B}" class="iw-today-line"/>
        ${chip(Math.min(X(last) + 8, W - 220), T + 14, `${fdate(last.fecha)} · ${fmt(last.peso_kg, 1)} kg`)}
        <g class="iw-cross" style="display:none">
          <line class="iw-cross-line" x1="0" x2="0" y1="${T}" y2="${H - B}"/>
          <circle class="iw-cross-dot" r="6"/>
        </g>
        <rect class="iw-hit" x="${L}" y="${T}" width="${W - L - R}" height="${H - T - B}" fill="transparent"/>
      `
    },
    buildChartSvg(c) {
      const geo = this.chartGeometry(c)
      if (!geo) return ''
      const { raw, W, H, L, R, T, B, horizon, s, y0, y1, X, Y, obs, last, projAll } = geo
      const path = (arr, key) => arr.map((r, i) => `${i ? 'L' : 'M'}${X(r.dia_lote).toFixed(1)},${Y(r[key]).toFixed(1)}`).join('')
      const band = `M${projAll.map(r => `${X(r.dia_lote).toFixed(1)},${Y(r.p90).toFixed(1)}`).join('L')}L${[...projAll].reverse().map(r => `${X(r.dia_lote).toFixed(1)},${Y(r.p10).toFixed(1)}`).join('L')}Z`
      // Legenda com fundo sólido: mantém o texto legível mesmo sobre as linhas/faixa do gráfico,
      // já que perto do objetivo várias curvas convergem e o texto puro fica ilegível ali.
      const chip = (x, y, text, anchor = 'middle', cls = '') => {
        const w = text.length * 6.3 + 14
        const rx = anchor === 'end' ? x - w : anchor === 'start' ? x : x - w / 2
        return `<rect class="iw-chart-chip-bg" x="${rx.toFixed(1)}" y="${(y - 12).toFixed(1)}" width="${w.toFixed(1)}" height="17" rx="3"/><text x="${x}" y="${y}" text-anchor="${anchor}" class="iw-chart-chip-text ${cls}">${text}</text>`
      }
      let grid = ''
      for (let v = y0; v <= y1; v += 20) {
        grid += `<line class="iw-grid" x1="${L}" x2="${W - R}" y1="${Y(v)}" y2="${Y(v)}"/><text x="${L - 8}" y="${Y(v) + 4}" text-anchor="end">${v}</text>`
      }
      let xt = ''
      for (let d = 1; d <= horizon; d += 14) {
        xt += `<line class="iw-axis" x1="${X(d)}" x2="${X(d)}" y1="${H - B}" y2="${H - B + 5}"/><text x="${X(d)}" y="${H - B + 18}" text-anchor="middle">dia ${d}</text><text x="${X(d)}" y="${H - B + 32}" text-anchor="middle">${fdate(addDays(c.fecha_ingreso, d - 1))}</text>`
      }
      const yObj = Y(raw.peso_objetivo_kg)
      const objX = c.dias_obj ? X(c.dias_obj) : null
      const objLabelAnchor = objX != null && objX > W - 120 ? 'end' : 'middle'
      const objLabelX = objLabelAnchor === 'end' ? Math.min(objX, W - R) : objX
      return `
        <text x="${L - 8}" y="${T - 2}" text-anchor="end" class="iw-chart-unit">kg</text>
        <text x="${W - R}" y="${T - 2}" text-anchor="end" class="iw-chart-goal-label">Objetivo ${fmt(raw.peso_objetivo_kg, 0)} kg</text>
        ${grid}<line class="iw-axis" x1="${L}" x2="${W - R}" y1="${H - B}" y2="${H - B}"/>${xt}
        <line x1="${L}" x2="${W - R}" y1="${yObj}" y2="${yObj}" class="iw-obj-line"/>
        <path d="${band}" class="iw-band"/>
        <path d="${path(s, 'pic_kg')}" class="iw-ref-line"/>
        <path d="${path(projAll, 'peso_kg')}" class="iw-proj-line"/>
        <path d="${path(obs, 'peso_kg')}" class="iw-obs-line"/>
        ${obs.map(r => `<circle cx="${X(r.dia_lote)}" cy="${Y(r.peso_kg)}" r="3.2" class="iw-obs-dot"/>`).join('')}
        <line x1="${X(last.dia_lote)}" x2="${X(last.dia_lote)}" y1="${T}" y2="${H - B}" class="iw-today-line"/>
        ${chip(X(last.dia_lote) + 6, T + 12, `hoje · dia ${last.dia_lote} · ${fmt(last.peso_kg, 1)} kg`, 'start', 'iw-chart-chip-ink')}
        ${chip(X(last.dia_lote) + 6, T + 30, `referência ${fmt(last.pic_kg, 1)} kg`, 'start', 'iw-chart-chip-ref')}
        ${objX ? `<circle cx="${objX}" cy="${yObj}" r="4" class="iw-obj-dot"/>${chip(objLabelX, yObj < T + 40 ? yObj + 20 : yObj - 12, `dia ${c.dias_obj}`, objLabelAnchor, 'iw-chart-chip-ink')}` : ''}
        <g class="iw-cross" style="display:none">
          <line class="iw-cross-line" x1="0" x2="0" y1="${T}" y2="${H - B}"/>
          <circle class="iw-cross-dot" r="6"/>
        </g>
        <rect class="iw-hit" x="${L}" y="${T}" width="${W - L - R}" height="${H - T - B}" fill="transparent"/>
      `
    },
    attachChartTooltip() {
      const svg = this.$refs.chart
      const tip = this.$refs.tip
      if (!svg || !tip || !this.detalheCorral) return
      const geo = this.raw?.penWeights
        ? { mode: 'pen', s: (this.detalheCorral.s || []).filter(row => row.peso_kg != null && row.fecha) }
        : this.chartGeometry(this.detalheCorral)
      if (!geo || (geo.mode === 'pen' && !geo.s.length)) return
      const hit = svg.querySelector('.iw-hit')
      const cross = svg.querySelector('.iw-cross')
      const crossLine = svg.querySelector('.iw-cross-line')
      const crossDot = svg.querySelector('.iw-cross-dot')
      if (!hit || !cross) return
      const pointAt = (clientX, clientY) => {
        const pt = svg.createSVGPoint()
        pt.x = clientX
        pt.y = clientY
        const p = pt.matrixTransform(svg.getScreenCTM().inverse())
        if (geo.mode === 'pen') {
          const circles = [...svg.querySelectorAll('.iw-obs-dot, .iw-flag-dot')]
          let best = geo.s[0]
          let bestDist = Infinity
          geo.s.forEach((row, index) => {
            const cx = Number(circles[index]?.getAttribute('cx'))
            const dist = Math.abs(cx - p.x)
            if (dist < bestDist) {
              best = row
              bestDist = dist
            }
          })
          const circle = circles[geo.s.indexOf(best)]
          return {
            row: best,
            x: Number(circle?.getAttribute('cx')),
            y: Number(circle?.getAttribute('cy')),
          }
        }
        const { horizon, X, Y, byDay, L, R, W } = geo
        const d = Math.max(1, Math.min(horizon, Math.round(1 + (p.x - L) / (W - L - R) * (horizon - 1))))
        const row = byDay[d]
        if (!row) return null
        return { row, x: X(row.dia_lote), y: Y(row.peso_kg) }
      }
      hit.onmousemove = e => {
        const hitPoint = pointAt(e.clientX, e.clientY)
        if (!hitPoint?.row) return
        const r = hitPoint.row
        const x = hitPoint.x
        const y = hitPoint.y
        cross.style.display = ''
        crossLine.setAttribute('x1', x)
        crossLine.setAttribute('x2', x)
        crossDot.setAttribute('cx', x)
        crossDot.setAttribute('cy', y)
        crossDot.setAttribute('class', 'iw-cross-dot ' + (r.flag ? 'flag' : r.fase === 'observado' ? 'obs' : 'proj'))

        const flagNote = r.flag ? `<div class="iw-tip-note">${penWeightFlagNote(r.flag)}</div>` : ''
        tip.style.display = 'block'
        tip.innerHTML = this.raw?.penWeights
          ? `<b>${fdateL(r.fecha)}</b><div class="iw-tip-row"><span>Peso previsto</span><b>${fmt(r.peso_kg, 1)} kg</b></div><div class="iw-tip-row"><span>p10–p90</span><span>${fmt(r.p10, 1)}–${fmt(r.p90, 1)}</span></div>${r.lecturas != null ? `<div class="iw-tip-row"><span>Fotos</span><span>${r.lecturas}</span></div>` : ''}${flagNote}`
          : `<b>${fdateL(r.fecha)}</b> · dia ${r.dia_lote}<div class="iw-tip-row"><span>${r.fase === 'observado' ? 'Peso observado' : 'Peso projetado'}</span><b>${fmt(r.peso_kg, 1)} kg</b></div><div class="iw-tip-row"><span>p10–p90</span><span>${fmt(r.p10, 1)}–${fmt(r.p90, 1)}</span></div>${r.lecturas != null ? `<div class="iw-tip-row"><span>Leituras</span><span>${r.lecturas}</span></div>` : ''}`
        const wrap = svg.parentElement.getBoundingClientRect()
        let left = (e.clientX - wrap.left) + 14
        if (left + 200 > wrap.width) left = (e.clientX - wrap.left) - 210
        tip.style.left = left + 'px'
        tip.style.top = Math.max(0, (e.clientY - wrap.top) - 40) + 'px'
      }
      hit.onmouseleave = () => {
        tip.style.display = 'none'
        cross.style.display = 'none'
      }
    },
  },
  created() {
    if (sensorScreenBlocked(this.$store.getters.getCurrentUser, this.$store.getters.getCompanySettings)) return
    this.barnTarget = this.barn || this.$store.getters.getBarnTarget
    this.load()
  },
}
</script>

<style scoped>
.iw-page {
  --iw-accent: #78b843;
  --iw-accent-dark: #1b4238;
  --iw-page-bg: #f2f2f2;
  --iw-surface: #ffffff;
  --iw-ink: #3e3e3e;
  --iw-ink-2: #6b6c6c;
  --iw-muted: #9a9a9a;
  --iw-hair: #e6e6e6;
  --iw-good: #78b843;
  --iw-bad: #a94949;
  --iw-warn: #e2bf65;
  --iw-neutral-bg: #ececec;
  color: var(--iw-ink);
  font-family: "Roboto", sans-serif;
}
.iw-page * { box-sizing: border-box; }
.iw-state { background: var(--iw-surface); border-radius: 8px; padding: 40px 20px; text-align: center; color: var(--iw-ink-2); }
.iw-retry { margin-top: 10px; background: var(--iw-accent); color: #fff; border: none; border-radius: 8px; padding: 8px 18px; cursor: pointer; font-weight: 500; }
.iw-retry:hover { background: var(--iw-accent-dark); }

.iw-demo { background: #fbf0dd; color: #8a5a00; border-radius: 8px; padding: 10px 16px; margin-bottom: 16px; display: flex; gap: 10px; flex-wrap: wrap; font-size: 13px; }
.iw-demo-tag { background: #8a5a00; color: #fff; padding: 2px 8px; border-radius: 4px; font-weight: 700; letter-spacing: .05em; font-size: 11px; }

.iw-top { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 16px 32px; background: var(--iw-surface); border-radius: 8px; padding: 18px 20px; margin-bottom: 20px; }
.iw-eyebrow { font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: var(--iw-muted); font-weight: 600; }
.iw-title { font-size: 28px; font-weight: 700; margin: 4px 0 6px; color: var(--iw-ink); }
.iw-sub { color: var(--iw-ink-2); font-size: 14px; }
.iw-sub b { color: var(--iw-ink); }
.iw-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: var(--iw-neutral-bg); color: var(--iw-ink-2); }
.iw-chip .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--iw-muted); }
.iw-chip.ok { background: #e9f4df; color: var(--iw-accent-dark); } .iw-chip.ok .dot { background: var(--iw-good); }
.iw-chip.neutral .dot { background: var(--iw-muted); }

section { margin-bottom: 28px; }
.iw-sec-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.iw-sec-head h3 { font-size: 18px; font-weight: 600; margin: 0; color: var(--iw-ink); }
.iw-hint { color: var(--iw-muted); font-size: 12px; }

.iw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
.iw-kpi { background: var(--iw-surface); border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,.04); padding: 14px 16px; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.iw-kpi-v { font-size: 28px; font-weight: 700; line-height: 1; color: var(--iw-ink); }
.iw-kpi-v small { font-size: 14px; font-weight: 500; color: var(--iw-ink-2); margin-left: 3px; }
.iw-kpi-d { font-size: 12px; color: var(--iw-ink-2); }

.iw-grupos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.iw-grupo { background: var(--iw-surface); border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,.04); padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; border: 2px solid transparent; cursor: pointer; }
.iw-grupo:hover { box-shadow: 0 4px 14px rgba(0,0,0,.08); }
.iw-grupo.alojado { border-color: var(--iw-accent); }
.iw-grupo-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.iw-grupo-title { font-size: 17px; font-weight: 700; }
.iw-grupo-date { font-size: 12px; color: var(--iw-ink-2); }
.iw-grupo dl { display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; margin: 0; font-size: 13px; }
.iw-grupo dt { color: var(--iw-muted); } .iw-grupo dd { margin: 0; font-weight: 600; text-align: right; }
.iw-grupo dd.empty { color: var(--iw-muted); font-weight: 400; }
.iw-cams { display: flex; gap: 6px; flex-wrap: wrap; }
.iw-cam-tag { font-size: 11px; padding: 2px 7px; border-radius: 4px; background: var(--iw-neutral-bg); color: var(--iw-ink-2); font-weight: 600; }
.iw-cam-tag.on { background: #e9f4df; color: var(--iw-accent-dark); }

.iw-delta { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; padding: 3px 7px; border-radius: 5px; color: var(--iw-ink-2); background: var(--iw-neutral-bg); white-space: nowrap; }
.iw-delta.good, .iw-dk .good, .iw-page .good { color: var(--iw-accent-dark); background: #e9f4df; }
.iw-delta.bad, .iw-dk .bad, .iw-page .bad { color: var(--iw-bad); background: #fbe6e3; }
.iw-delta.neutral { color: var(--iw-ink-2); }
.iw-unconfirmed-note { margin-top: 10px; color: var(--iw-ink-2); font-size: 13px; font-weight: 600; background: #f7f7f7; border-radius: 8px; padding: 16px; }

.iw-tabla-wrap { background: var(--iw-surface); border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,.04); overflow-x: auto; }
.iw-tabla { border-collapse: collapse; width: 100%; min-width: 900px; font-size: 13px; }
.iw-tabla th, .iw-tabla td { padding: 9px 10px; text-align: right; border-bottom: 1px solid var(--iw-hair); white-space: nowrap; }
.iw-tabla th.l, .iw-tabla td.l { text-align: left; }
.iw-tabla thead th { font-size: 11px; letter-spacing: .04em; text-transform: uppercase; color: var(--iw-muted); font-weight: 600; cursor: pointer; user-select: none; background: #f7f7f7; }
.iw-tabla thead th:hover { color: var(--iw-ink); }
.iw-tabla tbody tr { cursor: pointer; } .iw-tabla tbody tr:hover { background: #f7f7f7; } .iw-tabla tbody tr:last-child td { border-bottom: 0; }
.iw-tabla td.code { font-weight: 700; font-size: 14px; }
.iw-lect { display: inline-flex; align-items: center; gap: 5px; font-weight: 600; white-space: nowrap; font-size: 12px; }
.iw-lect .dot { width: 7px; height: 7px; border-radius: 50%; }
.iw-lect.ok { color: var(--iw-accent-dark); } .iw-lect.ok .dot { background: var(--iw-good); }
.iw-lect.warn { color: #8a5a00; } .iw-lect.warn .dot { background: var(--iw-warn); }
.iw-lect.bad { color: var(--iw-bad); } .iw-lect.bad .dot { background: var(--iw-bad); }
.iw-lect.none { color: var(--iw-muted); } .iw-lect.none .dot { background: var(--iw-muted); }

.iw-footer { border-top: 1px solid var(--iw-hair); padding-top: 14px; font-size: 12px; color: var(--iw-muted); }
.iw-legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: var(--iw-ink-2); }
.iw-legend span { display: inline-flex; align-items: center; gap: 6px; }
.iw-sw { width: 16px; height: 0; border-top: 2px solid var(--iw-ink); display: inline-block; }
.iw-sw.obs { border-color: var(--iw-accent); } .iw-sw.proj { border-color: #4e8ed4; border-top-style: dashed; }
.iw-sw.ref { border-color: #9a9a9a; border-top-style: dotted; }
.iw-sw.band { height: 8px; border: 0; background: rgba(78,142,212,.18); border-radius: 2px; }
.iw-sw.flag { width: 8px; height: 8px; border: 0; border-radius: 50%; background: #e2bf65; }
.good-txt { color: var(--iw-accent-dark); } .bad-txt { color: var(--iw-bad); }

.iw-dialog-backdrop { position: fixed; inset: 0; background: rgba(20,25,20,.55); display: flex; align-items: center; justify-content: center; z-index: 10050; padding: 20px; }
.iw-dialog { background: var(--iw-surface); border-radius: 14px; max-width: 920px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; box-shadow: 0 30px 80px -20px rgba(0,0,0,.4); }
.iw-dialog-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--iw-hair); }
.iw-dialog-head h3 { font-size: 26px; font-weight: 700; margin: 0 0 8px; }
.iw-dlg-meta { display: flex; flex-wrap: wrap; gap: 6px 0; font-size: 13px; color: var(--iw-ink-2); }
.iw-dlg-meta span { position: relative; padding: 0 10px; }
.iw-dlg-meta span:first-child { padding-left: 0; }
.iw-dlg-meta span + span::before { content: "·"; position: absolute; left: -2px; color: var(--iw-hair); }
.iw-close { border: none; background: #f2f2f2; color: var(--iw-ink-2); border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 18px; line-height: 1; flex: none; transition: background .15s, color .15s; }
.iw-close:hover { background: var(--iw-bad); color: #fff; }
.iw-dlg-kpis { display: grid; grid-template-columns: 1.2fr repeat(3, 1fr); gap: 10px; margin-bottom: 18px; }
.iw-dk { background: #f7f7f7; border-radius: 10px; padding: 12px 14px; min-width: 0; border: 1px solid transparent; }
.iw-dk-main { background: #eef6e6; border-color: #d9ecc7; }
.iw-dk .iw-k { font-size: 10px; letter-spacing: .05em; text-transform: uppercase; color: var(--iw-muted); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.iw-dk .iw-v { font-size: 21px; font-weight: 700; line-height: 1.15; margin-top: 4px; }
.iw-dk-main .iw-v { font-size: 26px; color: var(--iw-accent-dark); }
.iw-dk .iw-v small { font-size: 12px; color: var(--iw-ink-2); font-weight: 500; }
.iw-chart-panel { background: #fafafa; border: 1px solid var(--iw-hair); border-radius: 12px; padding: 16px 18px 12px; }
.iw-legend-chart { margin-bottom: 4px; }
.iw-chart-wrap { position: relative; margin-top: 8px; }
.iw-chart { width: 100%; height: auto; display: block; }
.iw-chart >>> text { font-family: "Roboto", sans-serif; font-size: 11px; fill: var(--iw-muted); }
.iw-chart >>> .iw-grid { stroke: #e9e9e9; stroke-width: 1; }
.iw-chart >>> .iw-axis { stroke: #cfcfcf; }
.iw-chart >>> .iw-band { fill: rgba(78,142,212,.14); }
.iw-chart >>> .iw-ref-line { fill: none; stroke: #9a9a9a; stroke-width: 1.5; stroke-dasharray: 2 4; }
.iw-chart >>> .iw-proj-line { fill: none; stroke: #4e8ed4; stroke-width: 2; stroke-dasharray: 6 4; }
.iw-chart >>> .iw-obs-line { fill: none; stroke: var(--iw-accent); stroke-width: 2.5; stroke-linejoin: round; }
.iw-chart >>> .iw-obs-dot { fill: var(--iw-accent); stroke: var(--iw-surface); stroke-width: 1.5; }
.iw-chart >>> .iw-flag-dot { fill: #e2bf65; stroke: #8a5a00; stroke-width: 1.5; }
.iw-chart >>> .iw-cross-dot.flag { stroke: #e2bf65; }
.iw-chart >>> .iw-obj-line { stroke: #cfcfcf; stroke-dasharray: 3 4; }
.iw-chart >>> .iw-obj-dot { fill: var(--iw-surface); stroke: #4e8ed4; stroke-width: 2; }
.iw-chart >>> .iw-today-line { stroke: #cfcfcf; }
.iw-chart >>> .iw-chart-unit { font-size: 11px; }
.iw-chart >>> .iw-chart-goal-label { font-size: 12px; font-weight: 700; fill: var(--iw-ink); }
.iw-chart >>> .iw-chart-chip-bg { fill: var(--iw-surface); opacity: .88; }
.iw-chart >>> .iw-chart-chip-text { font-weight: 600; fill: var(--iw-ink-2); }
.iw-chart >>> .iw-chart-chip-text.iw-chart-chip-ink { fill: var(--iw-ink); }
.iw-chart >>> .iw-chart-chip-text.iw-chart-chip-ref { fill: #9a9a9a; }
.iw-chart >>> .iw-cross-line { stroke: var(--iw-ink-2); stroke-width: 1; stroke-dasharray: 3 3; pointer-events: none; }
.iw-chart >>> .iw-cross-dot { fill: var(--iw-surface); stroke-width: 2.5; pointer-events: none; filter: drop-shadow(0 1px 3px rgba(0,0,0,.25)); }
.iw-chart >>> .iw-cross-dot.obs { stroke: var(--iw-accent); }
.iw-chart >>> .iw-cross-dot.proj { stroke: #4e8ed4; }
.iw-tip { position: absolute; pointer-events: none; background: var(--iw-ink); color: #fff; padding: 8px 10px; border-radius: 6px; font-size: 12px; line-height: 1.4; box-shadow: 0 8px 24px -12px rgba(0,0,0,.4); display: none; min-width: 160px; }
.iw-tip >>> .iw-tip-row, .iw-tip .iw-tip-row { display: flex; justify-content: space-between; gap: 12px; }

@media (max-width: 1100px) {
  .iw-grupos { grid-template-columns: repeat(2, 1fr); }
  .iw-dlg-kpis { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 700px) {
  .iw-top { padding: 14px; }
  .iw-title { font-size: 22px; }
  .iw-grupos { grid-template-columns: 1fr; }
  .iw-dlg-kpis { grid-template-columns: 1fr 1fr; }
}
</style>
